import {
  ClockIcon,
  HandRaisedIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import LandingCTA from '../landing/LandingCTA'
import LandingFooter from '../landing/LandingFooter'
import LandingHeader from '../landing/LandingHeader'

const costs = [
  {
    icon: UserPlusIcon,
    title: 'The guest in the back row',
    body: 'Someone visits us for the first time, and the first thing they watch is a room negotiating. They came to hear speeches',
  },
  {
    icon: HandRaisedIcon,
    title: 'The quiet member',
    body: 'Roles went to whoever spoke up fastest. The members who most needed the practice were the ones who sat it out',
  },
  {
    icon: ClockIcon,
    title: 'The evening after',
    body: 'Whatever we could not settle in the room turned into texts later that night. That part always landed on an officer',
  },
]

export default function About() {
  return (
    <div className="bg-white">
      <LandingHeader />
      <main>
        {/* Hero */}
        <div className="bg-gray-900">
          <div className="mx-auto max-w-7xl px-6 pb-24 pt-32 sm:pt-40 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h1 className="text-base font-semibold leading-7 text-indigo-400">
                About Speech Agenda
              </h1>
              <p className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">
                Built by a former VPE/President
              </p>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-300">
                Every week, our club opened the meeting by deciding who would
                take each role. It was friendly and familiar, but it quietly
                took time away from the meeting itself. I built Speech Agenda to
                give that time back
              </p>
            </div>
          </div>
        </div>

        {/* The story */}
        <div className="bg-white py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-2xl grid-cols-1 items-start gap-x-16 gap-y-10 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              <div className="mx-auto w-full max-w-xs lg:mx-0">
                <div className="overflow-hidden rounded-2xl bg-gray-100 shadow-xl shadow-gray-900/10 ring-1 ring-gray-200">
                  <img
                    src="/award-winning.png"
                    alt="Sergey, founder of Speech Agenda, holding a speech contest trophy"
                    className="aspect-[3/4] w-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-2">
                <h2 className="text-base font-semibold leading-7 text-indigo-600">
                  Who&apos;s behind it
                </h2>
                <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  The meeting that started it
                </p>
                <div className="mt-6 space-y-6 text-lg leading-8 text-gray-600">
                  <p>
                    Speech Agenda began during a Toastmasters meeting where we
                    spent far too much time filling roles. It was a recurring
                    problem, and I kept thinking: there has to be a better way.
                  </p>
                  <p>
                    The developer in me could not let the idea go. I found
                    myself thinking about it late at night, eager to build
                    something that could automate the repetitive work.
                    Spreadsheets are useful, but they are not mobile-friendly.
                    They also cannot send reminders, prepare digests, track
                    changes, or eliminate the need to rebuild the same schedule
                    repeatedly.
                  </p>
                  <p className="font-semibold text-gray-900">
                    So I built Speech Agenda
                  </p>
                  <p>
                    I soon discovered that our club was not alone. Club officers
                    everywhere were spending valuable time chasing
                    confirmations, filling open roles, and managing last-minute
                    changes—time that could be better spent helping members
                    practice, improve, and become more confident speakers.
                  </p>
                  <p>
                    I built the entire platform myself, with design support from
                    my friend Max, a professional designer. Our goal is simple:
                    take scheduling work off your plate and give your club more
                    time for what truly matters—public speaking.
                  </p>
                  <p>
                    Have an idea or a feature your club needs? Email{' '}
                    <a
                      href="mailto:support@speechagenda.com"
                      className="font-semibold text-indigo-600 hover:text-indigo-500">
                      support@speechagenda.com
                    </a>
                    . Your message goes directly to me.
                  </p>
                </div>
                <p className="mt-8 text-base font-semibold text-gray-900">
                  - Sergey, founder of Speech Agenda
                </p>
                <p className="text-sm leading-6 text-gray-500">
                  Former VP of Education &amp; Club President
                </p>
                {/* TODO(SPE-79): add the Sergeant at Arms quote here once written permission is granted, attributed as "— [Name], Sergeant at Arms of [club], the first club on Speech Agenda". */}
              </div>
            </div>
          </div>
        </div>

        {/* Why it exists */}
        <div className="bg-gray-50 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center">
              <h2 className="text-base font-semibold leading-7 text-indigo-600">
                Why I built it
              </h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Those minutes were never free
              </p>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                A few minutes of sorting roles sounds like nothing. But the same
                few minutes, every week, quietly cost us three things I cared
                about
              </p>
            </div>
            <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
                {costs.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl bg-white p-8 ring-1 ring-gray-900/5">
                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                      <item.icon
                        className="h-6 w-6 flex-none text-indigo-600"
                        aria-hidden="true"
                      />
                      {item.title}
                    </dt>
                    <dd className="mt-4 text-base leading-7 text-gray-600">
                      {item.body}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="mx-auto mt-16 max-w-2xl text-lg leading-8 text-gray-600 lg:text-center">
                None of it was hard. It was just nobody&apos;s job, so it became
                everybody&apos;s, out loud, at the worst possible moment. Speech
                Agenda settles it before the meeting starts - and it is free for
                clubs that just need their schedule online
              </p>
            </div>
          </div>
        </div>

        <LandingCTA />
      </main>
      <LandingFooter />
    </div>
  )
}
